import { useEffect, useState } from 'react';

import { Card, Statistic } from 'antd';
import type { FilterValue, TablePaginationConfig } from 'antd/es/table/interface';

import type {
  TEnrolledSectionParticipant,
  EEnrolledSectionParticipantStatus,
} from '@/api/endpoints/schools/schoolSelectionPanel';
import { useGetEnrolledSectionParticipantsQuery } from '@/api/endpoints/schools/schoolSelectionPanel';
import { ESelectionPanelTabs } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/_components/SelectionPanelHeader/SelectionPanelHeader';
import { ExpulsionModal } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/EnrolledTab/(pages)/EnrolledListPage/_components/ExpulsionModal/ExpulsionModal';
import { CustomTable, FetchDataWrapper } from '@/shared/components';

import styles from './EnrolledListPage.module.scss';
import { formatEnrolledStats, getColumnsData } from './helpers';

interface IProp {
  schoolId: number;
}

export const EnrolledListPage = ({ schoolId }: IProp) => {
  const [enrolledTableFilters, setEnrolledTableFilters] = useState<Record<string, FilterValue | null>>({});
  const [enrolledTablePagination, setEnrolledTablePagination] = useState<TablePaginationConfig>({
    pageSize: 10,
    current: 1,
    total: 0,
  });
  const [isExpulsionModalOpen, setIsExpulsionModalOpen] = useState(false);
  const [itemToOperate, setItemToOperate] = useState<TEnrolledSectionParticipant | null>(null);

  const {
    data: enrolledData,
    isLoading: isEnrolledLoading,
    isError: isEnrolledError,
  } = useGetEnrolledSectionParticipantsQuery({
    schoolId,
    page: enrolledTablePagination.current,
    perPage: enrolledTablePagination.pageSize,
    statusFilter: enrolledTableFilters.status as EEnrolledSectionParticipantStatus[],
  });

  const enrolledStats = formatEnrolledStats(enrolledData?.meta.stats);

  const columns = getColumnsData({
    filters: enrolledTableFilters,
    openExpulsionModalCb: () => setIsExpulsionModalOpen(true),
    setItemToOperate,
  });

  useEffect(() => {
    if (enrolledData?.meta.pagination.total_count) {
      setEnrolledTablePagination((prev) => ({
        ...prev,
        total: enrolledData.meta.pagination.total_count,
      }));
    }
  }, [enrolledData?.meta.pagination.total_count]);

  return (
    <FetchDataWrapper
      isLoading={isEnrolledLoading}
      isError={isEnrolledError}
    >
      {enrolledData && (
        <div className={styles.wrapper}>
          <SelectionPanelHeader currentTab={ESelectionPanelTabs.Enrolled} />
          <p className={styles.title}>Зачисленные студенты</p>
          {enrolledStats && (
            <div className={styles['statistic-wrapper']}>
              {enrolledStats.map(({ id, title, value }) => (
                <Card
                  key={id}
                  className={styles['statistic-card']}
                  variant="borderless"
                >
                  <Statistic
                    title={title}
                    value={value}
                  />
                </Card>
              ))}
            </div>
          )}
          <CustomTable
            className={styles.table}
            rowKey="id"
            pagination={{ ...enrolledTablePagination }}
            dataSource={enrolledData.data}
            onChange={(pagination, filters, _, extra) => {
              setEnrolledTableFilters(filters);
              setEnrolledTablePagination({
                ...pagination,
                ...(extra.action === 'paginate' && pagination.pageSize !== enrolledTablePagination.pageSize
                  ? { current: 1 }
                  : {}),
              });
            }}
            columns={columns}
          />
        </div>
      )}
      {itemToOperate && (
        <ExpulsionModal
          schoolId={schoolId}
          itemToOperate={itemToOperate}
          isModalOpen={isExpulsionModalOpen}
          handleCancel={() => setIsExpulsionModalOpen(false)}
        />
      )}
    </FetchDataWrapper>
  );
};
